/**
 * 上传文本页 - UI/UX Pro Max 优化版 + 超长文本支持
 *
 * 设计系统：
 * - Soft UI Evolution 风格
 * - 支持 10-20MB 超长文本
 * - 分析进度实时显示
 *
 * 功能：
 * 1. 上传/粘贴参考文本
 * 2. 自动识别文本大小，选择分析模式
 * 3. 显示分析进度
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload as UploadIcon,
  Loader2,
  X,
  CheckCircle,
  FileText,
  AlertCircle,
  Settings,
  Clock,
  Zap,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';

// 本地存储键
const SETTINGS_KEY = 'novel-outline-settings';
const ENDPOINT_KEY = 'novel-outline-endpoint';

// 文本大小阈值
const LONG_TEXT_THRESHOLD = 100000; // 10 万字
const MAX_TEXT_SIZE = 20 * 1024 * 1024; // 20MB

interface Settings {
  apiKeys: {
    openai: string;
    glm: string;
    minimax: string;
  };
  defaultBackend: 'openai' | 'glm' | 'minimax';
  apiEndpoint?: string;
}

export default function Upload() {
  const navigate = useNavigate();
  const { setAnalyzedStructure, setIsAnalyzing, setError, isAnalyzing, error } = useAppStore();

  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState<{ step: string; percent: number } | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [encoding, setEncoding] = useState<'utf-8' | 'gbk' | 'auto'>('auto');

  const wordCount = text.length;
  const isLongText = wordCount > LONG_TEXT_THRESHOLD;
  const isValid = wordCount >= 100 && wordCount <= MAX_TEXT_SIZE;

  // 加载设置
  useEffect(() => {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  }, []);

  // 处理拖拽
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // 处理文件拖放
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, []);

  // 处理文件选择
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  // 读取文件内容
  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.txt')) {
      setError('请上传 .txt 格式的文本文件');
      return;
    }

    // 检查文件大小
    if (file.size > MAX_TEXT_SIZE) {
      setError(`文件过大，最大支持 ${Math.floor(MAX_TEXT_SIZE / 1024 / 1024)}MB`);
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      let content: string;
      let usedEncoding: string;

      if (encoding === 'auto') {
        // 自动检测编码
        // 先尝试 UTF-8
        try {
          const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
          content = utf8Decoder.decode(uint8Array);
          usedEncoding = 'UTF-8';

          // 检查是否有乱码特征（替换字符）
          if (content.includes('\uFFFD')) {
            throw new Error('可能不是 UTF-8');
          }
        } catch {
          // UTF-8 失败，尝试 GBK
          try {
            const gbkDecoder = new TextDecoder('gbk', { fatal: true });
            content = gbkDecoder.decode(uint8Array);
            usedEncoding = 'GBK';
          } catch {
            // 都失败了，使用 UTF-8 容错模式
            const utf8Decoder = new TextDecoder('utf-8', { fatal: false });
            content = utf8Decoder.decode(uint8Array);
            usedEncoding = 'UTF-8 (容错)';
          }
        }
      } else {
        // 使用指定编码
        const decoder = new TextDecoder(encoding, { fatal: false });
        content = decoder.decode(uint8Array);
        usedEncoding = encoding.toUpperCase();
      }

      console.log(`文件读取成功，使用编码: ${usedEncoding}`);
      setText(content);
      setTitle(file.name.replace('.txt', ''));
    } catch (err) {
      setError('文件读取失败: ' + (err instanceof Error ? err.message : '未知错误'));
    }
  };

  // 获取 API 端点
  const getApiEndpoint = (): string => {
    // 优先从 localStorage 获取端点配置
    const savedEndpoint = localStorage.getItem(ENDPOINT_KEY);
    if (savedEndpoint) return savedEndpoint;

    // 其次从设置中获取
    if (settings?.apiEndpoint) return settings.apiEndpoint;

    // 默认使用生产环境端点
    return 'https://novel-outline-api.yaoyouwei7.workers.dev';
  };

  // 获取 API Key
  const getApiKey = (): { backend: string; apiKey: string; baseUrl?: string; model?: string } | null => {
    if (!settings) return null;

    const backend = settings.defaultBackend;
    const apiKey = settings.apiKeys[backend];

    if (!apiKey?.trim()) return null;
    return {
      backend,
      apiKey,
      baseUrl: settings.apiKeys[`${backend}BaseUrl` as keyof typeof settings.apiKeys] as string | undefined,
      model: settings.apiKeys[`${backend}Model` as keyof typeof settings.apiKeys] as string | undefined,
    };
  };

  // 提交分析
  const handleSubmit = async () => {
    if (!isValid) return;

    setIsAnalyzing(true);
    setError(null);
    setProgress(null);

    try {
      const apiConfig = getApiKey();

      // 根据文本大小选择 API
      if (isLongText) {
        // 超长文本使用长文本分析 API
        await handleLongTextAnalysis(apiConfig);
      } else {
        // 普通文本使用标准分析
        await handleStandardAnalysis(apiConfig);
      }

      // 跳转到生成页
      navigate('/generate');
    } catch (err) {
      setError(err instanceof Error ? err.message : '分析失败');
    } finally {
      setIsAnalyzing(false);
      setProgress(null);
    }
  };

  // 标准分析（10万字以内）
  const handleStandardAnalysis = async (apiConfig: { backend: string; apiKey: string; baseUrl?: string; model?: string } | null) => {
    setProgress({ step: '连接服务器...', percent: 10 });

    const apiEndpoint = getApiEndpoint();
    console.log('Using API endpoint:', apiEndpoint);

    let response: Response;
    try {
      response = await fetch(`${apiEndpoint}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          title: title || undefined,
          ...(apiConfig && {
            backend: apiConfig.backend,
            apiKey: apiConfig.apiKey,
            baseUrl: apiConfig.baseUrl,
            model: apiConfig.model,
          }),
        }),
      });
    } catch (networkError) {
      throw new Error(`网络连接失败，无法访问 ${apiEndpoint}。请检查网络或 API 端点设置。`);
    }

    setProgress({ step: '分析文本结构...', percent: 50 });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error?.message || result.error || '分析失败');
    }

    setProgress({ step: '完成', percent: 100 });
    setAnalyzedStructure(result.data.structure, result.data.analysis as unknown as string);
  };

  // 超长文本分析（10万字以上）
  const handleLongTextAnalysis = async (apiConfig: { backend: string; apiKey: string; baseUrl?: string; model?: string } | null) => {
    // 超长文本必须提供 API Key
    if (!apiConfig) {
      throw new Error('超长文本分析需要配置 API Key，请先在设置页面配置');
    }

    setProgress({ step: '连接服务器...', percent: 5 });

    const apiEndpoint = getApiEndpoint();
    console.log('Using API endpoint for long text:', apiEndpoint);

    let response: Response;
    try {
      response = await fetch(`${apiEndpoint}/api/analyze/long`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          title: title || undefined,
          backend: apiConfig.backend,
          apiKey: apiConfig.apiKey,
          baseUrl: apiConfig.baseUrl,
          model: apiConfig.model,
        }),
      });
    } catch (networkError) {
      throw new Error(`网络连接失败，无法访问 ${apiEndpoint}。请检查网络或 API 端点设置。`);
    }

    setProgress({ step: '分析超长文本中...', percent: 20 });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error?.message || result.error || '分析失败');
    }

    setProgress({ step: '完成', percent: 100 });

    // 长文本分析返回完整的结果结构
    const data = result.data;
    setAnalyzedStructure(data.structure, data.metadata.analysisMethod);
  };

  // 清空文本
  const handleClear = () => {
    setText('');
    setTitle('');
    setProgress(null);
  };

  // 跳过上传
  const handleSkip = () => {
    navigate('/generate');
  };

  // 格式化文件大小
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* 标题 */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">上传参考文本</h1>
          <p className="text-slate-500">
            上传你喜欢的爆款网文，AI 会分析其结构和爽点设计
          </p>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium mb-1">分析失败</p>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
              >
                关闭
              </button>
            </div>
          </div>
        )}

        {/* API Key 提示 */}
        {isLongText && !getApiKey() && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-800 font-medium mb-1">需要配置 API Key</p>
              <p className="text-sm text-amber-700 mb-2">
                超长文本（{Math.floor(wordCount / 10000)}万字）分析需要使用您自己的 API Key。
              </p>
              <Link
                to="/settings"
                className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800"
              >
                <Settings className="w-4 h-4" />
                前往设置
              </Link>
            </div>
          </div>
        )}

        {/* 编码选择 */}
        <div className="flex items-center gap-4 bg-slate-50 rounded-xl p-3 border border-slate-100">
          <span className="text-sm text-slate-600">文件编码：</span>
          <div className="flex gap-2">
            <button
              onClick={() => setEncoding('auto')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                encoding === 'auto'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              自动检测
            </button>
            <button
              onClick={() => setEncoding('utf-8')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                encoding === 'utf-8'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              UTF-8
            </button>
            <button
              onClick={() => setEncoding('gbk')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                encoding === 'gbk'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              GBK
            </button>
          </div>
          {encoding === 'auto' && (
            <span className="text-xs text-slate-400">（如遇乱码请手动选择编码）</span>
          )}
        </div>

        {/* 拖拽区域 */}
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-slate-300 hover:border-primary-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".txt"
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />

          <div className="text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${
              dragActive ? 'bg-primary-100' : 'bg-slate-100'
            }`}>
              <UploadIcon className={`w-8 h-8 ${dragActive ? 'text-primary-500' : 'text-slate-400'}`} />
            </div>
            <p className="text-slate-600 mb-2">
              拖拽 .txt 文件到此处，或点击上传
            </p>
            <p className="text-sm text-slate-400">
              支持 100 字 - 20MB（约 600 万字）
            </p>
          </div>
        </div>

        {/* 或分隔线 */}
        <div className="flex items-center gap-4">
          <div className="flex-1 border-t border-slate-200" />
          <span className="text-slate-400 text-sm">或直接粘贴文本</span>
          <div className="flex-1 border-t border-slate-200" />
        </div>

        {/* 标题输入 */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            文本标题（可选）
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="给参考文本起个名字"
            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* 文本输入 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-slate-700">
              参考文本
            </label>
            <div className="flex items-center gap-3">
              {wordCount > 0 && (
                <button
                  onClick={handleClear}
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                >
                  清空
                </button>
              )}
              <span
                className={`text-sm font-medium ${
                  wordCount < 100
                    ? 'text-slate-400'
                    : wordCount > MAX_TEXT_SIZE
                      ? 'text-red-500'
                      : isLongText
                        ? 'text-amber-500'
                        : 'text-green-500'
                }`}
              >
                {wordCount.toLocaleString()} 字
                {isLongText && ' (超长文本)'}
              </span>
            </div>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="粘贴你想要分析的网文文本..."
            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[300px] resize-y font-serif leading-relaxed"
          />
          {!isValid && wordCount > 0 && (
            <p className="text-sm text-red-500 mt-1">
              {wordCount < 100
                ? '文本太短，至少需要 100 字'
                : `文本超过 20MB 限制（当前 ${formatSize(wordCount)}）`}
            </p>
          )}
        </div>

        {/* 分析进度 */}
        {isAnalyzing && progress && (
          <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
              <span className="text-sm font-medium text-primary-700">{progress.step}</span>
            </div>
            <div className="h-2 bg-primary-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress.percent}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            {isLongText && (
              <div className="flex items-center gap-2 mt-3 text-xs text-primary-600">
                <Clock className="w-4 h-4" />
                <span>超长文本分析预计需要 5-10 分钟，请耐心等待</span>
              </div>
            )}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={handleSkip}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
            跳过
          </button>

          <button
            onClick={handleSubmit}
            disabled={!isValid || isAnalyzing || (isLongText && !getApiKey())}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all cursor-pointer ${
              isLongText && !getApiKey()
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600 text-white shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                分析中...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                {isLongText ? '开始长文本分析' : '开始分析'}
              </>
            )}
          </button>
        </div>

        {/* 功能说明 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-slate-500" />
              <span className="font-medium text-slate-700">智能章节识别</span>
            </div>
            <p className="text-sm text-slate-500">
              自动识别章节边界，提取结构信息
            </p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-amber-500" />
              <span className="font-medium text-slate-700">爽点分布分析</span>
            </div>
            <p className="text-sm text-slate-500">
              提取关键爽点，分析节奏设计
            </p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-5 h-5 text-primary-500" />
              <span className="font-medium text-slate-700">自定义 API</span>
            </div>
            <p className="text-sm text-slate-500">
              配置自己的 LLM API Key，支持超长文本
            </p>
          </div>
        </div>

        {/* 提示 */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <p className="text-sm text-blue-700 font-medium mb-2">分析说明</p>
          <ul className="text-sm text-blue-600 space-y-1">
            <li>• 上传参考文本是可选的，你可以跳过直接生成大纲</li>
            <li>• 超长文本（10万字以上）会自动启用分块采样分析</li>
            <li>• API Key 仅用于调用 LLM，不会被存储或传输到其他地方</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}
